(ns app.front.view.workspace.neighbour-properties
  (:require [app.domain.property.model :as property-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.system-of-units.neighbour-radius-defaults :as neighbour-subs]
            [app.front.view.system-of-units.toggle :as sou-toggle]
            [app.front.view.workspace.map :as workspace-map]
            [app.front.view.workspace.subs :as ws-subs]
            [core.domain.system-of-units :as sou]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(defn- neighbour-id [property]
  (workspace-model/neighbour-id property (<sub [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])))


(rf/reg-event-fx ::add-neighbour-property-view
  (fn [_ [_ app]]
    (let [workspace-id (-> app :entity/parent :workspace/id)]
      {:route/push {:route :view/neighbour-properties :params {:workspace-id workspace-id}}})))


(rf/reg-event-fx ::fetch-layer-property-id
  (fn [{:keys [db]} [_ workspace-id]]
    {:db           (assoc-in db [::layer-property-id-lookups workspace-id] {:loading? true})
     :comm/command {:command          :property/neighbour-layer-property-id
                    :data             {:workspace-id workspace-id}
                    :on-success       [::fetch-layer-property-id.success workspace-id]
                    :finally-dispatch [::fetch-layer-property-id.done workspace-id]}}))


(rf/reg-event-db ::fetch-layer-property-id.success
  (fn [db [_ workspace-id _ response]]
    (assoc-in db [::layer-property-id-lookups workspace-id]
              (assoc response :fetched? true :loading? false))))


(rf/reg-event-db ::fetch-layer-property-id.done
  (fn [db [_ workspace-id]]
    (assoc-in db [::layer-property-id-lookups workspace-id :loading?] false)))


(rf/reg-sub ::layer-property-id-lookup
  (fn [db [_ workspace-id]]
    (get-in db [::layer-property-id-lookups workspace-id])))


(rf/reg-event-fx ::neighbours-changed
  (fn [{:keys [db]} [_ map-ids app neighbours]]
    (let [new-neighbours    (workspace-model/neighbours-after-map-change map-ids neighbours)
          old-neighbours    (set (keys map-ids))
          selected-property (get db ::selected-property)
          reset-flag-path   [::reset-in-progress? (-> app :entity/parent :db/id)]]
      (if (= old-neighbours new-neighbours)
        {:db (p/dissoc-in db reset-flag-path)}
        {:db       (-> db
                       (assoc ::adding? true)
                       (p/dissoc-in reset-flag-path))
         :dispatch [:app.front.process/signal
                    (:db/id app)
                    [:process.signal/edit-neighbour-properties
                     (p/assoc-if {:neighbours new-neighbours}
                                 ;; if no properties are currently selected on the map
                                 ;; the neighbours will be automatically added
                                 :reset? (nil? selected-property))]
                    {:on-success [::neighbours-changed.success]
                     :notify     {:success :neighbours/edited
                                  :error   :neighbours/neighbour-add-fail}}]}))))

(rf/reg-event-fx ::remove-neighbour
  (fn [_ [_ selected-properties app id]]
    (let [new-neighbours (disj (set selected-properties) id)]
      {:dispatch [:app.front.process/signal
                  (:db/id app)
                  [:process.signal/edit-neighbour-properties {:neighbours new-neighbours}]
                  {:on-success [::entities/apply-changes]
                   :notify     {:success :neighbours/edited}}]})))

(rf/reg-event-fx ::add-owner
  (fn [{:keys [db]} [_ app property-id]]
    (let [new-owner (get-in db [::new-owner property-id])]
      {:dispatch [:app.front.process/signal
                  (:db/id app)
                  [:process.signal/add-property-owner (assoc new-owner :property-id property-id)]
                  {:on-success [::owner-add.success property-id]
                   :notify     {:success :neighbours/edited}}]})))

(rf/reg-event-fx ::save-owner
  (fn [{:keys [db]} [_ app owner-id property-id]]
    (let [owner (get-in db [::owner owner-id])]
      {:dispatch [:app.front.process/signal
                  (:db/id app)
                  [:process.signal/edit-property-owner (-> owner
                                                           (dissoc :edit?)
                                                           (assoc :owner-id owner-id :property-id property-id))]
                  {:on-success [::owner-save.success owner-id]
                   :notify     {:success :neighbours/edited}}]})))

(rf/reg-event-fx
  ::map-click
  (fn [_ [_ click-coordinates domain-group-id]]
    {:comm/command {:command    :address/point-search
                    :data       {:domain-group-id domain-group-id
                                 :municipality    domain-group-id
                                 :coordinates     click-coordinates
                                 :property-types  [:property.type/property]
                                 :sourceCRS       "3857"}
                    :on-success [::property-selected]}}))

(rf/reg-event-db ::property-selected
  (fn [db [_ _ response]]
    (let [prop (-> response :property)]
      (assoc db ::selected-property (:local-id prop)))))

(rf/reg-event-fx ::neighbours-changed.success
  (fn [{:keys [db]} [_ status response]]
    {:dispatch [::entities/apply-changes status response]
     :db       (dissoc db ::adding?)}))

(rf/reg-event-fx ::owner-add.success
  (fn [{:keys [db]} [_ property-id status response]]
    {:dispatch [::entities/apply-changes status response]
     :db       (-> db
                   (update ::new-owner dissoc property-id)
                   (update ::add-owner? dissoc property-id))}))

(rf/reg-event-fx ::owner-save.success
  (fn [{:keys [db]} [_ owner-id status response]]
    {:dispatch [::entities/apply-changes status response]
     :db       (-> db
                   (update ::owner dissoc owner-id))}))

(rf/reg-event-db ::reset-neighbour-list
  (fn [db [_ workspace]]
    ;; This causes a chain reaction:
    ;; - the workspace flag not being set will cause ::ws-subs/neighbour-ids to return an empty coll
    ;; - this coll is passed on to the map component where neighbour selection is triggered because of it
    ;; - the map component returns the new neighbours which are passed onto :process.signal/edit-neighbour-properties
    ;; - the new properties are outjected to the front, updating the page
    ;; - the workspace (flag) is exported as well, preventing the reset from bleeding into other map updates
    (assoc-in db [::reset-in-progress? (:db/id workspace)] true)))

(rf/reg-event-fx ::set-neighbour-radius-override
  (fn [_ [_ fsm-target value]]
    {:dispatch [:app.front.process/signal
                fsm-target
                [:process.signal/set-neighbour-radius-override
                 (cond-> {} ; No value = remove override
                   value (assoc :radius value))]
                {:on-success [::entities/apply-changes]
                 :notify     {:success :neighbours/radius-edited}}]}))

(rf/reg-event-db ::toggle-edit
  (fn [db [_ id]]
    (assoc-in db [::owner id :edit?] (-> db (get-in [::owner id :edit?]) boolean not))))

(rf/reg-event-db ::toggle-adding-owner
  (fn [db [_ id]]
    (assoc-in db [::add-owner? id] (-> db (get-in [::add-owner? id]) boolean not))))

(rf/reg-event-db ::set-new-owner-value
  (fn [db [_ id key value]]
    (assoc-in db [::new-owner id key] value)))

(rf/reg-event-db ::set-owner-value
  (fn [db [_ id key value]]
    (assoc-in db [::owner id key] value)))

(rf/reg-sub ::selected-property
  #(get % ::selected-property))

(rf/reg-sub ::adding?
  #(get % ::adding?))

(rf/reg-sub ::reset-in-progress?
  #(get % ::reset-in-progress?))

(rf/reg-sub ::add-owner-visible?
  (fn [db [_ id]]
    (get-in db [::add-owner? id])))

(rf/reg-sub ::edit?
  (fn [db [_ id]]
    (get-in db [::owner id :edit?])))

(rf/reg-sub ::radius-in-meters
  :<- [::ws-subs/workspace]
  :<- [::sou-toggle/default-system-of-units]
  :<- [::neighbour-subs/default-neighbour-radius]
  (fn [[workspace default-system-of-units default-radius] _]
    (sou/convert-value
      (-> sou/conversions :m default-system-of-units first)
      :metric
      (or (:workspace/neighbour-radius workspace)
          (some-> workspace :workspace/category-group-config :group.config/neighbour-radius)
          default-radius))))

(defn address-block [tr addr roll]
  [:<>
   [:div.flex-1.mr-4
    [:div.my-2.clear-both (tr [:workspace/address])]
    [:div.clear-both addr]]
   [:div.flex-1
    [:div.my-2.clear-both (tr [:property/id])]
    [:div.clear-both roll]]])

(defn person-block [{:keys [tr owner app property-id]}]
  (let [{:keys [:db/id :property-owner/name :property-owner/address]} owner
        edit?                                                         (<sub [::edit? id])
        edit-tr                                                       (if edit? :cancel :edit)]
    [:div
     {:class (futil/cs "my-2 clear-both leading-8"
                       (when edit? "border-dashed border-2 border-ui-gray"))}
     [:div
      {:class "p-2 flex bg-white"}
      (if edit?
        [:div.flex-1.pr-4 [ui/text-input {:id            "edit-name"
                                          :label-text    (tr [:neighbours/edit-name])
                                          :default-value name
                                          :on-change     #(>evt [::set-owner-value id :name (futil/tval %)])}]]
        [:div.flex-1 name])
      (if edit?
        [:div.flex-1 [ui/text-input {:id            "edit-address"
                                     :label-text    (tr [:neighbours/edit-address])
                                     :default-value address
                                     :on-change     #(>evt [::set-owner-value id :address (futil/tval %)])}]]
        [:div.flex-1 address])
      [:div.flex-1.text-right
       (when (not edit?)
         [:span.uppercase.mr-4 (cond (= :owner-source/user (:property-owner/owner-source owner))
                                     (tr [:neighbours/manually-added])
                                     (:property-owner/edited? owner)
                                     (tr [:neighbours/owner-edited]))])
       (cc/button {:kind             :ghost
                   :icon-position    :right
                   :size             :small
                   :icon             (if edit? i/remove i/pen)
                   :icon-description (tr [edit-tr])
                   :on-click         #(>evt [::toggle-edit id])}
                  (tr [edit-tr]))
       (when edit?
         (cc/button {:kind             :primary
                     :icon-position    :right
                     :size             :small
                     :icon             i/check
                     :icon-description (tr [:neighbours/update])
                     :on-click         #(>evt [::save-owner app id property-id])}
                    (tr [:neighbours/update])))
       (when (not edit?)
         [ui/modal-confirmation-button
          {:tr                  tr
           :id                  "delete-owner"
           :modal-title         (tr [:neighbours/remove-owner])
           :trigger-button-opts {:disabled false
                                 :size     :small
                                 :kind     :ghost
                                 :icon     i/trash}
           :modal-class         "text-left"
           :trigger-button-text (tr [:neighbours/remove])
           :on-yes-click        #(>evt [:app.front.process/signal
                                        app
                                        [:process.signal/remove-property-owner {:owner-id    id
                                                                                :property-id property-id}]
                                        {:notify {:success :neighbours/owner-removed}}])}
          (tr [:neighbours/remove-owner-confirm])])]]]))

(defn add-person-block [{:keys [app id tr adding?]}]
  (when adding?
    [:div.bg-light-gray.border-dashed.border-2.border-ui-gray
     [:div
      {:class "p-2 flex bg-white"}
      [:div.flex-1.pr-4 [ui/text-input {:id         "edit-name"
                                        :label-text (tr [:neighbours/edit-name])
                                        :on-change  #(>evt [::set-new-owner-value id :name (futil/tval %)])}]]
      [:div.flex-1 [ui/text-input {:id         "edit-address"
                                   :label-text (tr [:neighbours/edit-address])
                                   :on-change  #(>evt [::set-new-owner-value id :address (futil/tval %)])}]]
      [:div.flex-1.text-right.pt-4
       (cc/button {:kind             :ghost
                   :icon-position    :right
                   :size             :small
                   :icon             i/remove
                   :icon-description (tr [:cancel])
                   :on-click         #(>evt [::toggle-adding-owner id])}
                  (tr [:cancel]))
       (cc/button {:kind             :primary
                   :icon-position    :right
                   :size             :small
                   :icon             i/plus
                   :icon-description (tr [:neighbours/add-owner])
                   :on-click         #(>evt [::add-owner app id])}
                  (tr [:neighbours/add-owner]))]]]))

(defn controls [{:keys [selected-properties app neighbour system-added-properties]} tr]
  (let [id (neighbour-id neighbour)]
    [:<>
     (when (some #(= (:property/id %) (:property/id neighbour)) system-added-properties)
       [:div.flex-2.uppercase.text-left.mt-4 (tr [:neighbours/system-added])])
     [:div.flex-1.text-right
      [ui/modal-confirmation-button
       {:tr                  tr
        :id                  "delete-owner"
        :modal-title         (tr [:neighbours/remove-property])
        :trigger-button-opts {:disabled false
                              :size     :small
                              :kind     :ghost
                              :icon     i/trash}
        :modal-class         "text-left"
        :trigger-button-text (tr [:neighbours/remove])
        :on-yes-click        #(>evt [::remove-neighbour selected-properties app id])}
       (tr [:neighbours/remove-confirm])]]]))

(defn render-neighbour [{:keys [neighbour selected-property tr app] :as props}]
  (let [class       "mb-2 p-2 rounded-md border-2 border-solid"
        active?     (= selected-property (neighbour-id neighbour))
        property-id (:db/id neighbour)
        adding?     (<sub [::add-owner-visible? property-id])]
    [:div
     {:class (futil/cs class
                       (when active? "bg-ui-blue-10 border-ui-blue-60")
                       (when (not active?) "bg-ui-light-gray border-ui-gray"))}
     [:div.mb-2
      [:div.flex.flex-wrap
       [address-block tr (-> neighbour property-model/full-address) (-> neighbour :property/id)]
       [:div.hidden.lg:block.flex-1
        [:div.flex [controls props tr]]]]
      (if (or (seq (:property/owners neighbour))
              adding?)
        [:div
         [:div.mt-4.mb-2 (tr [:application.property/owners])]
         (for-frag [owner (:property/owners neighbour)]
           [person-block (util/mmap owner tr active? app property-id)])
         [add-person-block (util/mmap {:id property-id} app tr adding?)]]
        [:div.mt-4.mb-2 (tr [:application.property/no-owners])])
      [:div.block.lg:hidden.flex
       [controls props tr]]
      (when (not (<sub [::add-owner-visible? property-id]))
        (cc/button {:icon-position    :right
                    :icon             i/plus
                    :icon-description ""
                    :class-name       "my-2"
                    :on-click         #(>evt [::toggle-adding-owner property-id])}
                   (tr [:neighbours/add-owner])))]]))

(defn render-radius-selector
  "Workspace specific override for the default radius configured by the municipality admin"
  [{:keys [tr app]}]
  (r/with-let [*edited-radius (r/atom (-> app :entity/parent :workspace/neighbour-radius))]
    (let [workspace      (:entity/parent app)
          default-radius (<sub [::municipality/default-neighbour-radius workspace])
          max-radius     (<sub [::neighbour-subs/max-neighbour-radius])]
      [:div.my-2.p-2.pr-0.pt-0.border-solid.border-2.border-ui-light-gray.flex.flex-wrap.neighbour-radius-container
       [:div.mt-5.ml-2.mr-4
        (tr [:neighbours/neighbour-radius-override])]
       [ui/number-input
        {:id           "neighbour-radius-override"
         :value        @*edited-radius
         :min          0
         :max          max-radius
         :placeholder  default-radius
         :unit-label   (<sub [::sou-toggle/label-in-default-system :m])
         :invalid-text (tr [:group-admin/neighbour-radius-invalid] [max-radius])
         :invalid      (and (number? @*edited-radius)
                            (not (workspace-model/valid-neighbour-radius? max-radius @*edited-radius)))
         :on-change    #(reset! *edited-radius (->> % futil/tval futil/parse-int))
         :on-blur      #(>evt [::set-neighbour-radius-override app (->> % futil/tval futil/parse-int)])
         :light        true}]])))

(defn reset-neighbour-list-button [{:keys [tr workspace]}]
  [ui/modal-confirmation-button
   {:id                  "reset-neighbour-list-button"
    :tr                  tr
    :on-yes-click        #(>evt [::reset-neighbour-list workspace])
    :trigger-button-text (tr [:neighbours/reset-list])
    :trigger-button-opts {:kind       :tertiary
                          :icon       i/refresh
                          :class-name "my-2 ml-2"}}
   (tr [:neighbours/reset-warning])])

(defn export-button [{:keys [tr neighbours workspace]}]
  (cc/button
    {:icon-position :right
     :icon          i/download
     :class-name    "my-2 ml-2"
     :disabled      (empty? neighbours)
     :href          (str "/api/command/property/download-neighbour-export?workspace-id=" (:workspace/id workspace))}
    (tr [:neighbours/export-list])))

(defn neighbour-list [{:keys [selected-properties workspace app]}]
  (let [tr                      (<sub [::ws-subs/tr])
        selected-property       (<sub [::selected-property])
        neighbours              (<sub [::ws-subs/neighbours selected-property])
        system-added-properties (:workspace/default-neighbour-properties workspace)]
    [:div
     [:div.flex.flex-wrap.justify-end
      [render-radius-selector (util/mmap tr app)]
      [reset-neighbour-list-button (util/mmap tr workspace)]
      [export-button (util/mmap tr neighbours workspace)]]
     (when (<sub [::adding?])
       [:div.w-full.flex.flex-row.justify-center
        (cc/loading-spinner {:with-overlay false})])
     (for-frag [{:keys [:property/id] :as neighbour} neighbours]
       ^{:key id}
       [render-neighbour (util/mmap tr
                                    selected-properties
                                    app
                                    neighbour
                                    selected-property
                                    system-added-properties)])]))

(defn- neighbours-section [tr]
  (let [workspace                  (<sub [::ws-subs/workspace])
        app                        (<sub [::ws-subs/workspace-target])
        selected-properties        (<sub [::ws-subs/neighbour-ids])
        map-ids                    (<sub [::ws-subs/neighbour-map-ids])
        radius                     (<sub [::radius-in-meters])
        neighbour-layer-id         "neighbour-layer"
        layers                     (<sub [::municipality/neighbour-layer])
        prop-id-key                (<sub [::municipality/property-identifier (workspace-model/customer-id workspace)])
        property                   (:workspace/primary-property workspace)
        property-coordinates       (when-some [location (:entity/location property)]
                                     [(:location.center/x location)
                                      (:location.center/y location)])
        workspace-coordinates      (when-some [location (:entity/location workspace)]
                                     [(:location.center/x location)
                                      (:location.center/y location)])
        map-center                 (or property-coordinates
                                       workspace-coordinates)
        road-property?             (= (:property/type property) :property.type/road)
        property-id-lookup?        (<sub [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])
        layer-property-id-lookup   (<sub [::layer-property-id-lookup (:workspace/id workspace)])
        layer-property-id-pending? (and property-id-lookup? (not road-property?) (not (false? (:loading? layer-property-id-lookup))))
        ;; Road properties are likely from ROW layer, so id is not usable for
        ;; neighbour search
        prop-id                    (when-not road-property?
                                     (if property-id-lookup? (:property-id layer-property-id-lookup) (neighbour-id property)))
        group-id                   (-> workspace :domain/authority-group :entity/parent :group/id)
        map-args                   {:extended?             true
                                    :center                map-center
                                    :layers                layers
                                    :neighbourLayerId      neighbour-layer-id
                                    :propertyIdentifierKey prop-id-key
                                    :workspaceProperty     prop-id
                                    :workspaceCoordinates  map-center
                                    :neighbourRadius       radius}]
    [:div.neighbours
     (when (and property-id-lookup? (not road-property?) (:property/id property)
                (:fetched? layer-property-id-lookup) (nil? (:property-id layer-property-id-lookup)))
       [ui/warning (tr [:neighbours/subject-not-resolved])])
     (cond
       layer-property-id-pending?
       [:div.map-boundary.mb-4.mr-6.w-full.bg-gray-400.flex.flex-shrink-0.items-center.justify-center.map-extended
        (cc/loading-spinner {:with-overlay false})]

       (and (seq layers) map-center prop-id-key)
       [:div.map-boundary.mb-4.mr-6.w-full.bg-gray-400.flex.flex-shrink-0.items-center.justify-center.map-extended
        [workspace-map/workspace-map
         (p/assoc-if
           (merge map-args
                  {:onNeighbouringPropertiesChanged #(>evt [::neighbours-changed map-ids app (-> % js->clj)])
                   :events                          [{:eventType     "singleclick"
                                                      :eventFunction #(>evt [::map-click (.-coordinate ^js %) group-id])}]})
           ;; Map component returns the neighbours according to the radius if :neighbouringProperties is empty
           :neighbouringProperties (some-> map-ids workspace-model/neighbour-map-seed-ids))]]

       :else
       (do (js/console.warn "Neighbour map args incomplete" (clj->js map-args))
           [ui/warning (tr [:neighbours/map-error])]))
     [neighbour-list (util/mmap selected-properties workspace app)]]))

(defn view [_]
  (let [tr (<sub [:tr])]
    [:div.cloudpermit-page
     [section/section
      "neighbours-section"
      {:tr tr}
      :neighbours/view-header
      [neighbours-section tr]]]))
